class SupplySelector extends HTMLElement {
  connectedCallback() {
    this.data = this.readData();
    this.cards = Array.from(this.querySelectorAll("[data-ss-card]"));
    this.variantId = this.dataset.variantId || "";

    const defaultIndex = parseInt(this.dataset.defaultOption || "1", 10) - 1;
    const checked = this.cards.findIndex((card) => {
      const input = card.querySelector("[data-ss-input]");
      return input && input.checked;
    });
    this.selected = checked >= 0 ? checked : Math.min(Math.max(defaultIndex, 0), this.cards.length - 1);

    this.onChange = (event) => {
      const card = event.target.closest("[data-ss-card]");
      if (!card) return;
      this.selected = this.cards.indexOf(card);
      this.apply();
    };
    this.addEventListener("change", this.onChange);

    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event && event.data ? event.data.variant : null;
        if (variant && variant.id) {
          this.variantId = String(variant.id);
          this.apply();
        }
      });
    }

    this.onVariantEvent = (event) => {
      const detail = event ? event.detail : null;
      const id = detail && detail.variant ? detail.variant.id : detail ? detail.id : null;
      if (id) {
        this.variantId = String(id);
        this.apply();
      }
    };
    document.addEventListener("variant:change", this.onVariantEvent);

    this.apply();
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
    document.removeEventListener("variant:change", this.onVariantEvent);
    if (typeof this.unsubscribe === "function") this.unsubscribe();
  }

  readData() {
    const script = this.querySelector("[data-supply-selector-data]");
    if (!script) return {};
    try {
      return JSON.parse(script.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  variantData() {
    return this.data[String(this.variantId)] || this.data[Object.keys(this.data)[0]] || {};
  }

  apply() {
    const variant = this.variantData();

    const available = this.cards.filter((card) => variant[card.dataset.quantity]);
    if (available.length && !variant[(this.cards[this.selected] || {}).dataset?.quantity]) {
      this.selected = this.cards.indexOf(available[0]);
    }

    this.cards.forEach((card, index) => {
      const quantity = card.dataset.quantity;
      const option = variant[quantity];

      card.hidden = !option;
      if (!option) {
        card.classList.remove("is-active");
        return;
      }

      const active = index === this.selected;
      card.classList.toggle("is-active", active);
      const input = card.querySelector("[data-ss-input]");
      if (input) input.checked = active;

      this.setText(card, "[data-ss-title]", option.title);
      this.setText(card, "[data-ss-price]", option.price);

      const note = card.querySelector("[data-ss-note]");
      if (note) {
        const savePercent = (option.savePercent || "").trim();
        note.textContent = savePercent || note.dataset.ssNoteFallback || "";
        note.classList.toggle("wt-ss__note--save", savePercent.length > 0);
      }

      const compare = card.querySelector("[data-ss-compare]");
      if (compare) {
        const text = (option.compare || "").trim();
        compare.textContent = text;
        compare.hidden = text.length === 0;
      }

      const perDay = card.querySelector("[data-ss-per-day]");
      if (perDay) {
        const text = (option.perDay || "").trim();
        const hasCompare = (option.compare || "").trim().length > 0;
        perDay.textContent = text;
        perDay.hidden = hasCompare || text.length === 0;
      }
    });

    const current = this.currentOption();
    this.syncQuantity(current ? current.quantity : 1);
    this.broadcast(current);
  }

  currentOption() {
    const card = this.cards[this.selected];
    if (!card) return null;
    const variant = this.variantData();
    return variant[card.dataset.quantity] || null;
  }

  setText(card, selector, value) {
    const el = card.querySelector(selector);
    if (el && typeof value === "string") el.textContent = value;
  }

  quantityInput() {
    const form = this.closest("form") || document.querySelector('form[action*="/cart/add"]');
    if (!form) return null;

    let input = form.querySelector('input[name="quantity"]');
    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = "quantity";
      input.setAttribute("data-ss-quantity", "");
      form.appendChild(input);
    }
    return input;
  }

  syncQuantity(quantity) {
    const input = this.quantityInput();
    if (!input) return;
    const value = String(quantity || 1);
    if (input.value === value) return;
    input.value = value;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  broadcast(option) {
    if (!option) return;
    const payload = {
      id: this.dataset.ssId || "",
      variantId: this.variantId,
      quantity: option.quantity,
      price: option.price || "",
      compare: option.compare || "",
      perDay: option.perDay || "",
      servings: option.servings || "",
      savePercent: option.savePercent || "",
      saveAmount: option.saveAmount || ""
    };
    window.wtSupplyState = payload;
    document.dispatchEvent(new CustomEvent("wt:supply:change", { detail: payload }));
  }
}

if (!customElements.get("supply-selector")) {
  customElements.define("supply-selector", SupplySelector);
}
